import React, { useState, useRef, useEffect } from "react";

interface Project {
  id: number;
  title: string;
  shortDesc: string;
  fullDesc: string;
  image: string;
  icon: string;
  techStack: string[];
  features: string[];
  screenshots: string[];
  github: string;
}

interface StickyProjectScrollProps {
  projects: Project[];
}

export const StickyProjectScroll: React.FC<StickyProjectScrollProps> = ({ projects }) => {
  const [activeProject, setActiveProject] = useState(0);
  const projectsContainerRef = useRef<HTMLDivElement>(null);
  const projectRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize project refs
  useEffect(() => {
    projectRefs.current = projectRefs.current.slice(0, projects.length);
  }, [projects.length]);

  // Simplified approach - just handle clicks
  useEffect(() => {
    // Initial setup - no scroll detection needed
  }, []);


  // Reference to the content container to reset scroll
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Handle project selection
  const selectProject = (index: number) => {
    setActiveProject(index);
    // Reset scroll position when switching projects
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  };
  
  // State for carousel
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Reset carousel when project changes
  useEffect(() => {
    setCurrentSlide(0);
  }, [activeProject]);
  
  // Carousel navigation
  const nextSlide = () => {
    if (!projects[activeProject].screenshots) return;
    setCurrentSlide((prev) => 
      prev === projects[activeProject].screenshots.length - 1 ? 0 : prev + 1
    );
  };
  
  const prevSlide = () => {
    if (!projects[activeProject].screenshots) return;
    setCurrentSlide((prev) => 
      prev === 0 ? projects[activeProject].screenshots.length - 1 : prev - 1
    );
  };


  return (
    <div className="w-full min-h-[80vh] relative">
      <div className="flex flex-col md:flex-row">
        {/* Left side - Project list */}
        <div className="w-full md:w-1/3 lg:w-1/4 md:pr-8">
          <div className="space-y-4 pb-8">
            {projects.map((project, index) => (
              <div 
                key={project.id}
                className={`cursor-pointer p-4 rounded-lg transition-all duration-300 ${activeProject === index 
                  ? 'bg-[#800000]/10 border-l-4 border-[#800000] shadow-md transform scale-[1.02]' 
                  : 'hover:bg-[#f5f5f0]/70 hover:border-l-4 hover:border-[#800000]/30'}`}
                onClick={() => selectProject(index)}
              >
                <div className="flex items-center">
                  <div className={`mr-3 ${activeProject === index ? 'text-[#800000]' : 'text-gray-400'}`}>
                    <i className={project.icon}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-lg font-semibold ${activeProject === index ? 'text-[#800000]' : 'text-gray-700'}`}>
                      {project.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{project.shortDesc}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {project.techStack.slice(0, 2).map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-2 py-1 bg-[#800000]/10 text-xs rounded-full text-[#800000]"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  {activeProject === index && (
                    <div className="text-[#800000] ml-2">
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right side - Fixed project details */}
        <div className="w-full md:w-2/3 lg:w-3/4 md:sticky md:top-24 md:self-start md:h-[calc(100vh-12rem)]">
          <div className="bg-[#f5f5f0] overflow-hidden h-full flex flex-col transition-all duration-500 ease-in-out">

            {/* Project header */}
            <div className="relative h-[250px] overflow-hidden rounded-lg">
              <img
                src={projects[activeProject].image}
                alt={projects[activeProject].title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-[#800000] text-white p-2 rounded-full">
                    <i className={projects[activeProject].icon}></i>
                  </div>
                  <h2 className="text-3xl font-bold text-white">
                    {projects[activeProject].title}
                  </h2>
                </div>
              </div>
            </div>

            {/* Project content */}
            <div 
              ref={contentRef} 
              className="p-6 md:p-8 overflow-y-auto flex-grow scrollbar-container"
              style={{ scrollbarWidth: 'thin' }}
            >
              <div className="max-w-3xl mx-auto">
                {/* Overview */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-[#800000] mb-4">Overview</h3>
                  <p className="text-gray-700 leading-relaxed">
                    {projects[activeProject].fullDesc}
                  </p>
                </div>
                
                {/* Tech Stack */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-[#800000] mb-4">Tech Stack</h3>
                  <div className="flex flex-wrap gap-2">
                    {projects[activeProject].techStack.map((tech, techIndex) => (
                      <span
                        key={techIndex}
                        className="px-3 py-1 bg-[#800000]/10 text-[#800000] rounded-full text-sm hover:bg-[#800000] hover:text-white transition-all duration-300"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Key Features */}
                <div className="mb-8">
                  <h3 className="text-xl font-semibold text-[#800000] mb-4">Key Features</h3>
                  <ul className="space-y-2 text-gray-700">
                    {projects[activeProject].features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <span className="mr-2 text-[#2f6b2f]">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Screenshots Carousel */}
                {projects[activeProject].screenshots.length > 0 && (
                  <div className="mb-8">
                    <h3 className="text-xl font-semibold text-[#800000] mb-4">Screenshots</h3>
                    <div className="relative rounded-lg overflow-hidden h-[300px] md:h-[400px]">
                      {/* Carousel container */}
                      <div className="relative w-full h-full">
                        {/* Slides */}
                        {projects[activeProject].screenshots.map((screenshot, index) => (
                          <div 
                            key={index} 
                            className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                          >
                            <img 
                              src={screenshot} 
                              alt={`${projects[activeProject].title} screenshot ${index + 1}`}
                              className="w-full h-full object-cover" 
                            />
                          </div>
                        ))}
                        
                        {/* Navigation arrows */}
                        <button 
                          onClick={prevSlide}
                          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-[#800000] p-3 rounded-full shadow-md transition-all duration-300"
                          aria-label="Previous slide"
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                        <button 
                          onClick={nextSlide}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-[#800000] p-3 rounded-full shadow-md transition-all duration-300"
                          aria-label="Next slide"
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                        
                        {/* Slide indicators */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-20 flex space-x-2">
                          {projects[activeProject].screenshots.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentSlide(index)}
                              className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-[#800000] w-4' : 'bg-white/70 hover:bg-white'}`}
                              aria-label={`Go to slide ${index + 1}`}
                            ></button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* GitHub Link */}
                <div className="mt-8">
                  <a
                    href={projects[activeProject].github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 px-5 py-2.5 bg-[#800000] text-white rounded-lg hover:bg-[#2f6b2f] transition-colors duration-300"
                  >
                    <i className="fab fa-github mr-2"></i>
                    <span>View on GitHub</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
